import { Component, Input, OnInit } from '@angular/core';
import { NgStyle } from '@angular/common';

type SizesGap = number | 'default' | 'section';

@Component({
  selector: 'app-grid',
  imports: [NgStyle],
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
})
export class GridComponent implements OnInit {
  @Input() display: 'grid' | 'flex' = 'grid';
  @Input('display-xs') displayXs?: 'grid' | 'flex';
  @Input('display-sm') displaySm?: 'grid' | 'flex';
  @Input('display-md') displayMd?: 'grid' | 'flex';
  @Input('display-lg') displayLg?: 'grid' | 'flex';
  @Input() template?: string = 'none';
  @Input('template-xs') templateXs?: string;
  @Input('template-sm') templateSm?: string;
  @Input('template-md') templateMd?: string;
  @Input('template-lg') templateLg?: string;
  @Input() direction?: string = 'row';
  @Input('direction-xs') directionXs?: string;
  @Input('direction-sm') directionSm?: string;
  @Input('direction-md') directionMd?: string;
  @Input('direction-lg') directionLg?: string;
  @Input() align?: string = 'normal';
  @Input('align-xs') alignXs?: string;
  @Input('align-sm') alignSm?: string;
  @Input('align-md') alignMd?: string;
  @Input('align-lg') alignLg?: string;
  @Input() justify?: string = 'normal';
  @Input('justify-xs') justifyXs?: string;
  @Input('justify-sm') justifySm?: string;
  @Input('justify-md') justifyMd?: string;
  @Input('justify-lg') justifyLg?: string;
  @Input() wrap?: string = 'nowrap';
  @Input('wrap-xs') wrapXs?: string;
  @Input('wrap-sm') wrapSm?: string;
  @Input('wrap-md') wrapMd?: string;
  @Input('wrap-lg') wrapLg?: string;
  @Input() gap?: SizesGap;
  @Input('gap-xs') gapXs?: SizesGap;
  @Input('gap-sm') gapSm?: SizesGap;
  @Input('gap-md') gapMd?: SizesGap;
  @Input('gap-lg') gapLg?: SizesGap;
  @Input('row-gap') rowGap?: SizesGap;
  @Input('row-gap-xs') rowGapXs?: SizesGap;
  @Input('row-gap-sm') rowGapSm?: SizesGap;
  @Input('row-gap-md') rowGapMd?: SizesGap;
  @Input('row-gap-lg') rowGapLg?: SizesGap;
  @Input('col-gap') colGap?: SizesGap;
  @Input('col-gap-xs') colGapXs?: SizesGap;
  @Input('col-gap-sm') colGapSm?: SizesGap;
  @Input('col-gap-md') colGapMd?: SizesGap;
  @Input('col-gap-lg') colGapLg?: SizesGap;
  @Input() padding?: string;
  @Input('padding-xs') paddingXs?: string;
  @Input('padding-sm') paddingSm?: string;
  @Input('padding-md') paddingMd?: string;
  @Input('padding-lg') paddingLg?: string;
  @Input() margin?: string;
  @Input('margin-xs') marginXs?: string;
  @Input('margin-sm') marginSm?: string;
  @Input('margin-md') marginMd?: string;
  @Input('margin-lg') marginLg?: string;
  @Input('max-width') maxWidth?: string;
  @Input() width?: string;
  public calculatedGap?: number;
  public calculatedRowGap?: number;
  public calculatedColGap?: number;
  public finalGap?: string;
  public gapFinal?: string;
  public gapFinalLg?: string;
  public gapFinalMd?: string;
  public gapFinalSm?: string;
  public gapFinalXs?: string;
  public test?: string;

  setGaps(value: 'default' | 'section' | number | undefined): string | undefined {
    return value === 'default' ? 'var(--grid-component-gap)' : value === 'section' ? 'var(--main-gap)' : value ? value + 'px' : undefined;
  }

  ngOnInit(): void {
    this.displayLg = this.displayLg ?? this.display;
    this.displayMd = this.displayMd ?? this.displayLg;
    this.displaySm = this.displaySm ?? this.displayMd;
    this.displayXs = this.displayXs ?? this.displaySm;
    this.templateLg = this.templateLg ?? this.template;
    this.templateMd = this.templateMd ?? this.templateLg;
    this.templateSm = this.templateSm ?? this.templateMd;
    this.templateXs = this.templateXs ?? this.templateSm;
    this.directionLg = this.directionLg ?? this.direction;
    this.directionMd = this.directionMd ?? this.directionLg;
    this.directionSm = this.directionSm ?? this.directionMd;
    this.directionXs = this.directionXs ?? this.directionSm;
    this.alignLg = this.alignLg ?? this.align;
    this.alignMd = this.alignMd ?? this.alignLg;
    this.alignSm = this.alignSm ?? this.alignMd;
    this.alignXs = this.alignXs ?? this.alignSm;
    this.justifyLg = this.justifyLg ?? this.justify;
    this.justifyMd = this.justifyMd ?? this.justifyLg;
    this.justifySm = this.justifySm ?? this.justifyMd;
    this.justifyXs = this.justifyXs ?? this.justifySm;
    this.wrapLg = this.wrapLg ?? this.wrap;
    this.wrapMd = this.wrapMd ?? this.wrapLg;
    this.wrapSm = this.wrapSm ?? this.wrapMd;
    this.wrapXs = this.wrapXs ?? this.wrapSm;
    this.paddingLg = this.paddingLg ?? this.padding;
    this.paddingMd = this.paddingMd ?? this.paddingLg;
    this.paddingSm = this.paddingSm ?? this.paddingMd;
    this.paddingXs = this.paddingXs ?? this.paddingSm;
    this.marginLg = this.marginLg ?? this.margin;
    this.marginMd = this.marginMd ?? this.marginLg;
    this.marginSm = this.marginSm ?? this.marginMd;
    this.marginXs = this.marginXs ?? this.marginSm;

    const gap = this.setGaps(this.gap);
    const gapLg = this.setGaps(this.gapLg);
    const gapMd = this.setGaps(this.gapMd);
    const gapSm = this.setGaps(this.gapSm);
    const gapXs = this.setGaps(this.gapXs);
    const rowGap = this.setGaps(this.rowGap);
    const rowGapLg = this.setGaps(this.rowGapLg);
    const rowGapMd = this.setGaps(this.rowGapMd);
    const rowGapSm = this.setGaps(this.rowGapSm);
    const rowGapXs = this.setGaps(this.rowGapXs);
    const colGap = this.setGaps(this.colGap);
    const colGapLg = this.setGaps(this.colGapLg);
    const colGapMd = this.setGaps(this.colGapMd);
    const colGapSm = this.setGaps(this.colGapSm);
    const colGapXs = this.setGaps(this.colGapXs);

    const gapFinalRow = rowGap ?? gap ?? '0';
    const gapFinalCol = colGap ?? gap ?? '0';
    this.gapFinal = gapFinalRow + ' ' + gapFinalCol;
    const gapFinalRowLg = rowGapLg ?? gapLg ?? gapFinalRow;
    const gapFinalColLg = colGapLg ?? gapLg ?? gapFinalCol;
    this.gapFinalLg = gapFinalRowLg + ' ' + gapFinalColLg;
    const gapFinalRowMd = rowGapMd ?? gapMd ?? gapFinalRowLg;
    const gapFinalColMd = colGapMd ?? gapMd ?? gapFinalColLg;
    this.gapFinalMd = gapFinalRowMd + ' ' + gapFinalColMd;
    const gapFinalRowSm = rowGapSm ?? gapSm ?? gapFinalRowMd;
    const gapFinalColSm = colGapSm ?? gapSm ?? gapFinalColMd;
    this.gapFinalSm = gapFinalRowSm + ' ' + gapFinalColSm;
    const gapFinalRowXs = rowGapXs ?? gapXs ?? gapFinalRowSm;
    const gapFinalColXs = colGapXs ?? gapXs ?? gapFinalColSm;
    this.gapFinalXs = gapFinalRowXs + ' ' + gapFinalColXs;
  }
}
